/**
 * Finite slider.
 * 
 * To use it, include the template iwdtheme4/base/templates/sliders/finite-slider.twig,
 * passing in the appropriate variables to configure it. Make sure initFiniteSliders()
 * is called somewhere.
 * 
 * In order to slide to items/groups from outside, you can trigger events
 * on the '.slider' wrap:
 * 	- slide-to-item (passing { itemIndex: number, noAnim?: boolean })
 * 	- slide-to-prev-item
 * 	- slide-to-next-item
 * 	- slide-to-group (passing { groupIndex: number, noAnim?: boolean })
 * 	- slide-to-prev-group
 * 	- slide-to-next-group
 *
 * You can listen for the '.slider' wrap's 'slide' event, which will contain
 * the following data:
 * 	- groupIndex
 *  - itemIndex
 * 	- maxIndex
 * 	- numGroups
 * 	- prevAllowed (bool)
 * 	- nextAllowed (bool)
 * 
 * If for some reason you need to trigger a slide event (and obtain the event data object),
 * you can trigger the '.slider' wrap's 'force-slide' event.
 * 
 */

import { throttle, ceil, debounce, floor, each, min, max, range } from 'lodash';
import {
	getWaitValueFromWrapAttr,
	getItemsPerGroupDataFromWrapAttr,
	getItemsPerGroup,
	highlightGroupControl,
	ItemsPerGroupSetting,
	ItemWidthMultiplier,
	getItemWidthMultipliersFromWrapAttr,
	getItemWidthMultiplier,
} from './slider-helpers';
import { zeroPadNumber } from './utils';
import * as Hammer from 'hammerjs';

declare const jQuery: JQueryStatic;
const $ = jQuery;
const $window = $(window);

export interface SliderSettings {
	disableSwipe?: boolean;
	returnToStart?: boolean;
	returnToEnd?: boolean;
}

const config = Object.freeze({
	dfItemsPerGroup: [
		{ minWindowWidth: 0, nItems: 1 },
	]
});

/**
 * Initializes all finite sliders.
 */
export function initFiniteSliders(): void {
	$('.finite-slider').each(function () {
		initSlider($(this));
	});
}

/**
 * 
 * @param $wrap Wrap for finite slider
 * @param settings SliderSettings object
 */
export function initSlider($wrap: JQuery, settings?: SliderSettings): void {
	settings = settings || {};

	if ((settings.disableSwipe === undefined) && $wrap.is('[data-disable-swipe]')) {
		settings.disableSwipe = true;
	}

	if ((settings.returnToStart === undefined) && $wrap.is('[data-return-to-start]')) {
		settings.returnToStart = true;
	}

	if ((settings.returnToEnd === undefined) && $wrap.is('[data-return-to-end]')) {
		settings.returnToEnd = true;
	}

	let xOffset = 0;

	const $slider = $wrap.find('.slider-items-slider');
	const autoSlideWait: number | undefined = getWaitValueFromWrapAttr($wrap);
	let autoSlideCancelled: boolean = false;
	const itemsPerGroupSettings: ItemsPerGroupSetting[] = getItemsPerGroupDataFromWrapAttr($wrap, config.dfItemsPerGroup);
	const itemWidthMultipliers: ItemWidthMultiplier[] = getItemWidthMultipliersFromWrapAttr($wrap);
	
	initSliderLayout($slider, $window, itemsPerGroupSettings, itemWidthMultipliers);

	let itemIndex = 0;
	let autoslideTimer: any;

	function getMaxIndex(): number {
		return calcMaxIndex($slider, $window, itemsPerGroupSettings);
	}

	function getNumGroups(): number {
		return ceil($slider.children().length / getItemsPerGroup($window, itemsPerGroupSettings));
	}

	function goTo(newIndex: number, forceChangeEvent: boolean = false, noAnim: boolean = false): void {
		clearTimeout(autoslideTimer);
		const maxIndex: number = getMaxIndex();
		newIndex = min([max([0, newIndex]), maxIndex]) || 0;
		slideToItem($slider, newIndex, noAnim, xOffset);
		const numGroups: number = getNumGroups();
		const groupIndex: number = getGroupIndex(newIndex, maxIndex, numGroups, $window, itemsPerGroupSettings);
		highlightGroupControl($wrap, groupIndex);
		setPrevNextDisabled($wrap, newIndex, maxIndex);
		const changed: boolean = itemIndex !== newIndex;
		itemIndex = newIndex;
		if (changed || forceChangeEvent) {
			$slider.trigger('slide', {
				groupIndex,
				itemIndex,
				maxIndex,
				numGroups,
				prevAllowed: itemIndex > 0,
				nextAllowed: itemIndex < maxIndex
			});
		}
		if (autoSlideWait && !autoSlideCancelled) {
			nextAutoSlide();
		}
	}

	function nextAutoSlide(): void {
		clearTimeout(autoslideTimer);
		if (autoSlideCancelled) {
			return;
		}
		autoslideTimer = setTimeout(() => {
			if (itemIndex < getMaxIndex()) {
				nextItem();
			}
			else {
				goTo(0);
			}
			nextAutoSlide();
		}, autoSlideWait);
	}

	function goToGroup(groupIndex: number, noAnim: boolean = false): void {
		goTo(groupIndex * getItemsPerGroup($window, itemsPerGroupSettings), false, noAnim);
	}

	const prevItem = () => {
		if (settings?.returnToEnd && (itemIndex <= 0)) {
			goTo(getMaxIndex());
		}
		else {
			goTo(itemIndex - 1);
		}
	};

	const nextItem = () => {
		if (settings?.returnToStart && (itemIndex >= getMaxIndex())) {
			goTo(0);
		}
		else {
			goTo(itemIndex + 1);
		}
	};

	const prevGroup = () => {
		if (settings?.returnToEnd && (itemIndex <= 0)) {
			goTo(getMaxIndex());
		}
		else {
			goTo(itemIndex - getItemsPerGroup($window, itemsPerGroupSettings));
		}
	};

	const nextGroup = () => {
		if (settings?.returnToStart && (itemIndex >= getMaxIndex())) {
			goTo(0);
		}
		else {
			goTo(itemIndex + getItemsPerGroup($window, itemsPerGroupSettings));
		}
	};

	const getCurrentGroupIndex = (): number => {
		const maxIndex: number = getMaxIndex();
		const numGroups: number = getNumGroups();
		return getGroupIndex(itemIndex, maxIndex, numGroups, $window, itemsPerGroupSettings);
	};

	initPrevAndNextGroupButtons($wrap, prevGroup, nextGroup);
	initNavControls($wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);

	let setSwipeDisabled = (disabled: boolean) => { };
	if (!settings.disableSwipe) {
		setSwipeDisabled = initSwipe($wrap, prevGroup, nextGroup);
	}
	highlightGroupControl($wrap, 0);
	setPrevNextDisabled($wrap, 0, getMaxIndex());

	$window.on('resize', debounce(() => {
		goTo(itemIndex, false, true);
	}, 50, { trailing: true }));

	$wrap.on('disable-swipe', () => {
		setSwipeDisabled(true);
	}).on('enable-swipe', () => {
		setSwipeDisabled(false);
	}).on('set-x-offset', (event, data) => {
		const newXOffset: number = typeof data?.xOffset === 'number' ? data.xOffset : 0;
		if (xOffset !== newXOffset) {
			xOffset = newXOffset;
			goTo(itemIndex, false, true);
		}
	});

	$wrap.addClass('show');

	$wrap.on('slide-to-item', (e, data) => {
		if (!data || data.itemIndex === undefined) {
			console.error('No itemIndex passed to slider slide-to-item handler');
			return;
		}
		goTo(data.itemIndex, false, !!data.noAnim);
	});

	$wrap.on('slide-to-prev-item', (e, data) => {
		prevItem();
	});

	$wrap.on('slide-to-next-item', (e, data) => {
		nextItem();
	});

	$wrap.on('slide-to-group', (e, data) => {
		if (data.groupIndex === undefined) {
			console.error('No groupIndex passed to slider slide-to-group handler');
			return;
		}
		goToGroup(data.groupIndex, !!data.noAnim);
	});

	$wrap.on('slide-to-prev-group', (e, data) => {
		prevGroup();
	});

	$wrap.on('slide-to-next-group', (e, data) => {
		nextGroup();
	});

	$wrap.on('force-slide', (e, data) => {
		goTo(itemIndex, true);
	});

	if (autoSlideWait) {
		nextAutoSlide();
	}
}

function initSwipe($wrap: JQuery, prevGroup: Function, nextGroup: Function): (disabled: boolean) => void {
	if (typeof Hammer !== 'function') {
		console.error('HammerJS missing');
		return (isDisabled: boolean) => {};
	}

	try {
		let disabled: boolean = false;
		const wrap = $wrap[0];

		let hammer: any;

		const prevGroupWrapped = () => { prevGroup(); };
		const nextGroupWrapped = () => { nextGroup(); };

		const addOrRemoveHandlers = () => {
			if (disabled) {
				if (hammer) {
					hammer.off('swiperight', prevGroupWrapped);
					hammer.off('swipeleft', nextGroupWrapped);
					hammer.destroy();
				}
			}
			else {
				try {
					hammer = new Hammer(wrap, {});
					hammer.on('swiperight', prevGroupWrapped);
					hammer.on('swipeleft', nextGroupWrapped);
				} catch (error) {
					console.error('HammerJS slider error', error);
				}
			}
		};

		addOrRemoveHandlers();

		return (isDisabled: boolean) => {
			if (disabled !== isDisabled) {
				disabled = isDisabled;
				addOrRemoveHandlers();
			}
		};
	} catch (error) {
		console.error('HammerJS slider error', error);
		return (isDisabled: boolean) => {};
	}
}

function getGroupIndex(itemIndex: number, maxItemIndex: number, numGroups: number, $window: JQuery<Window & typeof globalThis>, itemsPerGroupSettings: ItemsPerGroupSetting[]): number {
	if (itemIndex >= maxItemIndex) {
		return numGroups - 1;
	}
	const groupIndex: number = floor(itemIndex / getItemsPerGroup($window, itemsPerGroupSettings));
	if (groupIndex === 0 && itemIndex > 0 && numGroups > 1) {
		return 1;
	}
	return groupIndex;
}

function initSliderLayout($slider: JQuery, $window: JQuery<Window & typeof globalThis>, itemsPerGroupSettings: ItemsPerGroupSetting[], itemWidthMultipliers: ItemWidthMultiplier[]): void {
	const fixLayout = () => setWidths($slider, $window, itemsPerGroupSettings, itemWidthMultipliers);
	fixLayout();
	if ($slider[0] && ($slider[0] as any).__iw_sliderresize) {
		$window.off('load resize', ($slider[0] as any).__iw_sliderresize);
	}
	const throttled = ($slider[0] as any).__iw_sliderresize = throttle(fixLayout, 50);
	$window.on('load resize', throttled);
}

function initPrevAndNextGroupButtons($wrap: JQuery, prevGroup: () => void, nextGroup: () => void): void {
	$wrap.find('[data-slider-ctrl-prev]').on('click', function (event) {
		prevGroup();
		return false;
	});
	$wrap.find('[data-slider-ctrl-next]').on('click', function (event) {
		nextGroup();
		return false;
	});
}

function setPrevNextDisabled($wrap: JQuery, itemIndex: number, maxIndex: number): void {
	const $prev = $wrap.find('[data-slider-ctrl-prev]').removeClass('disabled');
	const $next = $wrap.find('[data-slider-ctrl-next]').removeClass('disabled');
	const shouldDisablePrev: boolean = itemIndex == 0;
	const shouldDisableNext: boolean = itemIndex == maxIndex;
	if (shouldDisablePrev) {
		$prev.addClass('disabled');
	}
	if (shouldDisableNext) {
		$next.addClass('disabled');
	}
	if (shouldDisablePrev && shouldDisableNext) {
		$wrap.addClass('all-nav-disabled');
	}
	else {
		$wrap.removeClass('all-nav-disabled');
	}
}

function initNavControls(
	$wrap,
	$slider,
	$window,
	goToGroup: (groupIndex: number) => void,
	getCurrentGroupIndex: () => number,
	itemsPerGroupSettings): void {
	initNavControlsForType('dot', $wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);
	initNavControlsForType('number', $wrap, $slider, $window, goToGroup, getCurrentGroupIndex, itemsPerGroupSettings);
}

// $sliderWrap - jQuery object for '.finite-slider' div
// $appendToThis - if provided, the new controls wrapper div will be append to this
// buttonTemplate - HTML template string for each nav button. Use {n} tag to interpolate number.
// Returns jQuery for new controls wrapper div
export function createNavControls($sliderWrap: JQuery, type?: 'dot' | 'number', $appendToThis?: JQuery, buttonTemplate?: string) {
	type = type || 'dot';
	const $slider = $sliderWrap.find('.slider-items-slider');
	const $wrap = $(`<div><div class="${type}-controls"><div></div></div></div>`);
	const itemsPerGroupSettings: ItemsPerGroupSetting[] = getItemsPerGroupDataFromWrapAttr($sliderWrap, config.dfItemsPerGroup);
	const goToGroup = (groupIndex: number) => {
		$slider.trigger('slide-to-group', { groupIndex });
	};
	let groupIndex: number = 0;
	const getCurrentGroupIndex = (): number => groupIndex;
	const $ctrlWrap = initNavControlsForType(type, $wrap, $slider, $(window), goToGroup, getCurrentGroupIndex, itemsPerGroupSettings, buttonTemplate);
	if ($appendToThis) {
		$appendToThis.append($wrap.children());
	}
	$sliderWrap.on('slide', function (event, data) {
		highlightGroupControl($ctrlWrap, data.groupIndex);
		groupIndex = data.groupIndex;
	});
	$ctrlWrap.on('nav-ctrl-reset', function (event, data) {
		if (!data || (data.groupIndex === undefined)) {
			throw new Error('No groupIndex on nav-ctrl-reset data');
		}
		highlightGroupControl($ctrlWrap, data.groupIndex);
		groupIndex = data.groupIndex;
	});
	highlightGroupControl($ctrlWrap, groupIndex);
	return $ctrlWrap;
}

function initNavControlsForType(
	type: 'dot' | 'number',
	$wrap: JQuery,
	$slider: JQuery,
	$window: JQuery<Window & typeof globalThis>,
	goToGroup: (groupIndex: number) => void,
	getCurrentGroupIndex: () => number,
	itemsPerGroupSettings: ItemsPerGroupSetting[],
	buttonTemplate?: string
	) {
	const $outerWrap = $wrap.find(`.${type}-controls`);
	const $ctrlWrap = $outerWrap.children();
	if ($ctrlWrap.length === 0) {
		return $outerWrap;
	}
	const isDot: boolean = (type === 'dot');
	const shouldZeroPadNumber: boolean = (type === 'number') && $wrap.hasClass('zero-pad-numbers');
	const reset = function reset(): void {
		const numItems: number = $slider.children().length;
		const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
		const numGroups: number = ceil(numItems / itemsPerGroup);
		if (numGroups < 2) {
			$ctrlWrap.css({ display: 'none' });
			return;
		}
		$ctrlWrap.empty().css({ display: '' });
		each(range(0, numGroups), (groupIndex: number) => {
			const numStr: string = shouldZeroPadNumber ? zeroPadNumber(groupIndex + 1) : (groupIndex + 1).toString();
			const html: string = buttonTemplate ?
				buttonTemplate.replace(/\{\s*n\s*\}/gi, numStr) :
				(isDot ? `<div class="dot"><div></div></div>` : `<div class="number"><div>${numStr}</div></div>`);
			$(html).appendTo($ctrlWrap).on('click', function () {
				goToGroup(groupIndex);
				return false;
			});
		});
		const groupIndex: number = getCurrentGroupIndex();
		highlightGroupControl($outerWrap, groupIndex);
		$outerWrap.trigger('nav-ctrl-reset', { groupIndex });
	};
	reset();
	$window.on('resize orientationchange load', throttle(reset, 50, { trailing: true }));
	return $outerWrap;
}

function calcMaxIndex($slider: JQuery, $window: JQuery<Window & typeof globalThis>, itemsPerGroupSettings: ItemsPerGroupSetting[]): number {
	const numItems: number = $slider.children().length;
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const numGroups: number = ceil(numItems / itemsPerGroup);
	if (numGroups < 2) {
		return 0;
	}
	const firstIndexOfLastGroup: number = itemsPerGroup * (numGroups - 1);
	const remainder: number = (numItems % itemsPerGroup); // No. of items in last group or 0 if last group is full
	if (remainder === 0) {
		return firstIndexOfLastGroup;
	}
	const numItemsMissingInLastGroup: number = itemsPerGroup - remainder;
	return firstIndexOfLastGroup - numItemsMissingInLastGroup;
}

function setWidths($slider: JQuery, $window: JQuery<Window & typeof globalThis>, itemsPerGroupSettings: ItemsPerGroupSetting[], itemWidthMultipliers: ItemWidthMultiplier[]): void {
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const $items = $slider.children();
	const numItems: number = $items.length;
	const sliderWidthPercent: number = (100 / itemsPerGroup) * numItems;
	$slider.css({ width: sliderWidthPercent + '%' });
	const itemWidthPercent: number = 100 / numItems * getItemWidthMultiplier($window, itemWidthMultipliers);
	$items.css({ width: itemWidthPercent + '%' });
}

function slideToItem($slider: JQuery, itemIndex: number, noAnim: boolean, xOffset: number): void {
	if (!noAnim) {
		performSlideTransition($slider, itemIndex, xOffset);
		return;
	}

	const $wrap = $slider.closest('.slider');
	const wrap = $wrap[0];

	if (!wrap) {
		console.error('No .slider wrap found during slideToAnim');
		return;
	}

	const clr = () => {
		clearTimeout((wrap as any)._sliderNoAnimTimer);
	};

	const timeout = (fn: Function) => {
		clr();
		(wrap as any)._sliderNoAnimTimer = setTimeout(fn, 20);
	};

	$wrap.addClass('no-anim');

	timeout(() => {
		performSlideTransition($slider, itemIndex, xOffset);
		timeout(() => {
			$wrap.removeClass('no-anim');
		});
	});
}

function performSlideTransition($slider: JQuery, itemIndex: number, xOffset: number): void {
	const $items = $slider.children();
	const x: number = (-itemIndex * ($items.outerWidth() || 0)) + xOffset;
	$slider.css({ transform: `translateX(${x}px)` });
}

export function syncFiniteSliders($master: JQuery, $slave: JQuery): void {
	if ($master.find('.slider-item').length !== $slave.find('.slider-item').length) {
		console.error('linkSliders failed because sliders have different no. items');
		return;
	}
	$master.on('slide', function (event, data) {
		const itemIndex: number = data.itemIndex;
		$slave.trigger('slide-to-item', { itemIndex });
	});
	$slave.trigger('disable-swipe');
}

export function initPrevNextButtonsForSlider($slider: JQuery, $prev?: JQuery, $next?: JQuery): void {
	if ($prev) {
		$prev.on('click', function () {
			$slider.trigger('slide-to-prev-group');
			return false;
		});
		$slider.on('slide', function (event, data) {
			data.prevAllowed ? $prev.removeClass('disabled') : $prev.addClass('disabled');
		});
	}
	if ($next) {
		$next.on('click', function () {
			$slider.trigger('slide-to-next-group');
			return false;
		});
		$slider.on('slide', function (event, data) {
			data.nextAllowed ? $next.removeClass('disabled') : $next.addClass('disabled');
		});
	}
	$slider.trigger('force-slide');
}
